import { Feature, Map } from "ol";
import GeoJSON from "ol/format/GeoJSON";
import { useEffect } from "react";
import getBackendURL from "@/api/api";
import { request } from "@/request";
import { colorMap, toStyle } from "../../styles";
import { useVectorLayer } from "../utils/useVectorLayer";

const format = new GeoJSON({
	dataProjection: "EPSG:4326",
	featureProjection: "EPSG:3857",
});

// GEOIDs of the counties stored in the `<qid>.COUNTIES` response
export function parseCountyGeoids(value: string): string[] {
	if (!value) return [];
	try {
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

// Selected counties are kept off the question layer so they are never edited
// or saved as shapes. Their outlines are looked up by GEOID.
export function useCountyLayer(map: Map | null, geoids: string[]) {
	const { source, layer } = useVectorLayer(map, {
		zIndex: 1,
		style: toStyle(colorMap.project_limits + "33", colorMap.project_limits),
	});

	useEffect(() => {
		source.clear();
		if (geoids.length === 0) return;

		const params = new URLSearchParams();
		for (const geoid of geoids) {
			params.append("geoid", geoid);
		}
		// a newer selection may arrive before this one's outlines do
		let stale = false;
		request(getBackendURL(`/esr/counties/shapes/?${params}`)).then(({ data }) => {
			if (stale) return;
			source.addFeatures(format.readFeatures(data) as Feature[]);
		});
		return () => {
			stale = true;
		};
	}, [source, geoids]);

	return { source, layer };
}
